import { createClient } from '@connectrpc/connect';
import { create } from '@bufbuild/protobuf';
import {
  BranchKind,
  CadFileKind,
  CadService,
  ListBranchNodesRequestSchema,
  ListCommitsRequestSchema,
  ListRepositoryBranchesRequestSchema,
  VersionControlService,
  type AssemblyNodeData,
  type BranchInfo,
  type CommitInfo,
  type RepositoryBranchInfo,
} from '@puppycad/proto';
import { grpcWebTransport } from '@/services/api/transport/grpc-web-transport';
import type {
  AssemblyBranchNodeListService,
  AssemblyChildNodeBranchSummary,
  AssemblyChildNodeCommit,
  AssemblyChildNodeSummary,
  AssemblyChildNodeType,
  ListAssemblyChildNodesInput,
  SwitchAdoptedNodeVersionInput,
} from '../types/assembly-branch-node-list-types';

const DEFAULT_CHILD_NODE_PAGE_SIZE = 999;
const DEFAULT_NODE_COMMIT_PAGE_SIZE = 100;
const BRANCH_NODE_BRANCH_KIND = BranchKind.NORMAL;

const cadClient = createClient(CadService, grpcWebTransport);
const versionClient = createClient(VersionControlService, grpcWebTransport);

export interface AssemblyBranchNodeListProjectContext {
  /** 当前项目 ID。 */
  readonly projectId: bigint;
  /** 当前打开装配节点所属仓库 ID。 */
  readonly repositoryId: bigint;
  /** 当前打开装配节点所属分支 ID。 */
  readonly branchId?: bigint;
  /** 当前打开装配节点对应的 HEAD 提交 ID。 */
  readonly currentCommitId?: string;
}

export interface CreateAssemblyBranchNodeListServiceOptions {
  /** 读取当前项目、仓库与分支上下文。 */
  readonly getProjectContext: () => AssemblyBranchNodeListProjectContext | null;
  /** 读取直接子节点时的页大小。 */
  readonly childNodePageSize?: number;
  /** 读取子节点提交记录时的页大小。 */
  readonly nodeCommitPageSize?: number;
}

interface EncodedAssemblyNodeKey {
  /** 子节点仓库 ID。 */
  readonly repositoryId: bigint;
  /** 子节点当前采纳分支 ID。 */
  readonly adoptedBranchId: bigint;
  /** 子节点当前采纳提交 ID。 */
  readonly adoptedCommitId: string;
  /** 子节点实例 UUID。 */
  readonly instanceUuid: string;
  /** 父装配 workAssemble 中的稳定结构 key；旧远端节点可为空。 */
  readonly nodeKey?: string;
}

export interface AssemblyChildNodeReference {
  /** 子节点仓库 ID。 */
  readonly repositoryId: string;
  /** 父装配当前采纳的分支 ID；没有采纳版本时为空字符串。 */
  readonly adoptedBranchId: string;
  /** 父装配当前采纳的提交 ID；没有采纳版本时为空字符串。 */
  readonly adoptedCommitId: string;
  /** 父装配中该子节点实例的 UUID。 */
  readonly instanceUuid: string;
  /** 父装配 workAssemble 中的稳定结构 key；旧远端节点可为空。 */
  readonly nodeKey?: string;
}

/**
 * 编码节点列表服务生成的子节点引用。
 *
 * 设计说明：节点列表 UI 的 nodeId 会携带当前采纳提交；当本地工作装配草稿切换采纳版本后，
 * 需要在不等待后端列表刷新的情况下同步更新 nodeId，避免后续弹窗再解码出旧提交。
 *
 * @param reference 子节点引用字段。
 * @returns 可供节点列表继续传递的前端节点 ID。
 */
export function encodeAssemblyChildNodeReference(reference: AssemblyChildNodeReference): string {
  const adoptedBranchId = reference.adoptedBranchId.trim()
    ? BigInt(reference.adoptedBranchId.trim())
    : 0n;

  return encodeAssemblyNodeKey({
    repositoryId: parsePositiveBigIntId(reference.repositoryId, 'node.repositoryId'),
    adoptedBranchId,
    adoptedCommitId: reference.adoptedCommitId,
    instanceUuid: reference.instanceUuid,
    nodeKey: reference.nodeKey,
  });
}

/**
 * 将字符串 ID 转为后端需要的 uint64。
 *
 * @param value 前端上下文里的字符串 ID。
 * @param fieldName 字段名称，用于错误定位。
 * @returns bigint ID。
 */
function parsePositiveBigIntId(value: string, fieldName: string): bigint {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    throw new Error(`${fieldName} 为空，无法调用分支与节点接口`);
  }

  try {
    const parsed = BigInt(normalizedValue);
    if (parsed <= 0n) {
      throw new Error('must be positive');
    }
    return parsed;
  } catch {
    throw new Error(`${fieldName} 必须是正整数：${value}`);
  }
}

/**
 * 将 protobuf Timestamp 转成页面消费的 ISO 字符串。
 *
 * @param timestamp 后端返回的 protobuf 时间。
 * @returns ISO 时间字符串；缺失或非法时返回空字符串。
 */
function timestampToIsoString(timestamp: CommitInfo['createdAt']): string {
  if (!timestamp) {
    return '';
  }

  const milliseconds = Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1_000_000);
  const date = new Date(milliseconds);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

/**
 * 编码节点身份。
 *
 * 设计说明：新版 `AssemblyNodeData` 没有单独的稳定 nodeId；页面层仍需要用一个字符串串起列表、
 * hover、高亮和版本弹窗，因此把协议身份字段收口在 service 边界。
 *
 * @param input 节点协议身份字段。
 * @returns 前端可稳定传递的节点 ID。
 */
function encodeAssemblyNodeKey(input: EncodedAssemblyNodeKey): string {
  const parts = [
    'repo',
    input.repositoryId.toString(),
    'branch',
    input.adoptedBranchId.toString(),
    'commit',
    encodeURIComponent(input.adoptedCommitId),
    'instance',
    encodeURIComponent(input.instanceUuid),
  ];
  const nodeKey = input.nodeKey?.trim();

  if (nodeKey) {
    parts.push('nodeKey', encodeURIComponent(nodeKey));
  }

  return parts.join('|');
}

/**
 * 解码 service 生成的节点 ID。
 *
 * @param nodeId 前端节点 ID。
 * @returns 节点协议身份字段。
 */
function decodeAssemblyNodeKey(nodeId: string): EncodedAssemblyNodeKey {
  const parts = nodeId.split('|');
  if (
    (parts.length !== 8 && parts.length !== 10)
    || parts[0] !== 'repo'
    || parts[2] !== 'branch'
    || parts[4] !== 'commit'
    || parts[6] !== 'instance'
    || (parts.length === 10 && parts[8] !== 'nodeKey')
  ) {
    throw new Error(`无法解析装配节点 ID：nodeId=${nodeId}`);
  }

  return {
    repositoryId: parsePositiveBigIntId(parts[1] ?? '', 'node.repositoryId'),
    adoptedBranchId: BigInt(parts[3] || '0'),
    adoptedCommitId: decodeURIComponent(parts[5] ?? ''),
    instanceUuid: decodeURIComponent(parts[7] ?? ''),
    ...(parts.length === 10 ? { nodeKey: decodeURIComponent(parts[9] ?? '') } : {}),
  };
}

/**
 * 解码节点列表服务生成的子节点引用。
 *
 * 设计说明：页面层只需要仓库、当前采纳版本和实例身份来定位模型树节点，具体 nodeId 编码格式仍由 service 维护。
 *
 * @param nodeId 前端节点 ID。
 * @returns 可供模型加载链路定位子节点的引用字段。
 */
export function decodeAssemblyChildNodeReference(nodeId: string): AssemblyChildNodeReference {
  const decoded = decodeAssemblyNodeKey(nodeId);

  return {
    repositoryId: decoded.repositoryId.toString(),
    adoptedBranchId: decoded.adoptedBranchId > 0n ? decoded.adoptedBranchId.toString() : '',
    adoptedCommitId: decoded.adoptedCommitId,
    instanceUuid: decoded.instanceUuid,
    ...(decoded.nodeKey ? { nodeKey: decoded.nodeKey } : {}),
  };
}

/**
 * 归一化节点类型。
 *
 * @param nodeType 后端返回的节点类型枚举。
 * @returns 前端卡片使用的节点类型。
 */
function normalizeAssemblyChildNodeType(nodeType: CadFileKind): AssemblyChildNodeType {
  return nodeType === CadFileKind.PART ? 'part' : 'assembly';
}

/**
 * 生成采纳版本显示文案。
 *
 * @param node 后端节点数据。
 * @returns 可直接展示的采纳版本文案。
 */
function resolveAdoptedVersionLabel(node: AssemblyNodeData): string {
  const commitId = node.adoptedCommitId.trim();
  if (!commitId) {
    return '--';
  }

  const branchName = node.adoptedBranchName.trim() || node.adoptedBranchId.toString();
  const commitInfo = node.adoptedCommitInfo.trim() || commitId;
  return `${branchName} / ${commitInfo}`;
}

/**
 * 将后端分支信息映射成节点卡片的负责分支摘要。
 *
 * @param branch 后端分支信息。
 * @returns 前端负责分支摘要。
 */
function toBranchSummary(branch: RepositoryBranchInfo): AssemblyChildNodeBranchSummary | null {
  const branchInfo = branch.branch;
  if (!branchInfo) {
    return null;
  }

  const branchName = branchInfo.branchName.trim() || branchInfo.branchId.toString();
  return {
    repositoryId: branchInfo.repositoryId.toString(),
    branchId: branchInfo.branchId.toString(),
    branchName,
    assigneeName: branchInfo.assigneeUsername.trim()
      || branchInfo.createdBy.trim()
      || '未分配',
    ...(branchInfo.assigneeUserId > 0n ? { assigneeUserId: branchInfo.assigneeUserId.toString() } : {}),
    canEdit: branchInfo.canEdit,
    status: branchInfo.canEdit ? 'working' : 'submitted',
  };
}

/**
 * 将后端节点数据映射成前端节点摘要。
 *
 * @param node 后端节点数据。
 * @param branches 子节点仓库的分支列表。
 * @returns 前端节点摘要。
 */
function toChildNodeSummary(
  node: AssemblyNodeData,
  branches: readonly AssemblyChildNodeBranchSummary[],
): AssemblyChildNodeSummary {
  const adoptedCommitId = node.adoptedCommitId.trim();
  const nodeId = encodeAssemblyNodeKey({
    repositoryId: node.repositoryId,
    adoptedBranchId: node.adoptedBranchId,
    adoptedCommitId,
    instanceUuid: node.instanceUuid.trim(),
  });

  return {
    repositoryId: node.repositoryId.toString(),
    nodeId,
    name: node.name.trim() || node.repositoryId.toString(),
    nodeType: normalizeAssemblyChildNodeType(node.nodeType),
    branchCount: node.branchCount,
    adoptedCommitId: adoptedCommitId || null,
    adoptedVersionLabel: resolveAdoptedVersionLabel(node),
    hasCommits: Boolean(adoptedCommitId),
    branches,
  };
}

/**
 * 解析分支显示名称。
 *
 * @param branch 后端分支信息。
 * @returns 分支名称。
 */
function resolveBranchName(branch: BranchInfo | undefined): string {
  return branch?.branchName.trim() || branch?.branchId.toString() || '默认分支';
}

/**
 * 根据提交图推导每条提交所属的展示分支。
 *
 * @param commits 后端本次返回的提交列表。
 * @param branches 后端本次命中的分支列表。
 * @returns commitId 到分支信息的映射。
 */
function buildCommitBranchMap(
  commits: readonly CommitInfo[],
  branches: readonly BranchInfo[],
): ReadonlyMap<string, BranchInfo> {
  const commitById = new Map<string, CommitInfo>();
  commits.forEach((commit) => {
    const commitId = commit.commitId.trim();
    if (commitId) {
      commitById.set(commitId, commit);
    }
  });

  const branchByCommitId = new Map<string, BranchInfo>();
  branches.forEach((branch) => {
    const headCommitId = branch.headCommitId.trim();
    if (headCommitId && !branchByCommitId.has(headCommitId)) {
      branchByCommitId.set(headCommitId, branch);
    }
  });

  branches.forEach((branch) => {
    const headCommitId = branch.headCommitId.trim();
    const pendingCommitIds = headCommitId ? [headCommitId] : [];
    const visitedCommitIds = new Set<string>();

    while (pendingCommitIds.length > 0) {
      const commitId = pendingCommitIds.pop();
      if (!commitId || visitedCommitIds.has(commitId)) {
        continue;
      }
      visitedCommitIds.add(commitId);

      // 分支 HEAD 直接命中的提交更能表达“该提交属于哪个分支”，父链补齐时不要覆盖它。
      if (!branchByCommitId.has(commitId)) {
        branchByCommitId.set(commitId, branch);
      }

      const commit = commitById.get(commitId);
      if (!commit) {
        continue;
      }

      commit.parentCommitIds.forEach((parentCommitId) => {
        const normalizedParentCommitId = parentCommitId.trim();
        if (normalizedParentCommitId) {
          pendingCommitIds.push(normalizedParentCommitId);
        }
      });
    }
  });

  return branchByCommitId;
}

/**
 * 将提交信息映射为子节点提交记录。
 *
 * @param commit 后端提交信息。
 * @param nodeId 前端节点 ID。
 * @param branch 分支信息。
 * @returns 子节点提交记录。
 */
function toNodeCommit(
  commit: CommitInfo,
  nodeId: string,
  branch: BranchInfo | undefined,
): AssemblyChildNodeCommit {
  const message = commit.message.trim();

  return {
    id: commit.commitId,
    nodeId,
    message: message || commit.commitId,
    authorName: commit.createdBy.trim(),
    committedAt: timestampToIsoString(commit.createdAt),
    branchId: (branch?.branchId ?? 0n).toString(),
    branchName: resolveBranchName(branch),
  };
}

/**
 * 按固定并发数处理列表。
 *
 * @param items 待处理列表。
 * @param concurrency 最大并发数。
 * @param mapper 单项处理函数。
 * @returns 与输入顺序一致的处理结果。
 */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex]!);
    }
  }

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/**
 * 创建装配体分支与节点真实接口服务。
 *
 * @param options 创建选项。
 * @returns 装配体分支与节点列表服务。
 */
export function createAssemblyBranchNodeListService(
  options: CreateAssemblyBranchNodeListServiceOptions,
): AssemblyBranchNodeListService {
  const childNodePageSize = options.childNodePageSize ?? DEFAULT_CHILD_NODE_PAGE_SIZE;
  const nodeCommitPageSize = options.nodeCommitPageSize ?? DEFAULT_NODE_COMMIT_PAGE_SIZE;

  /**
   * 读取当前项目上下文。
   *
   * @returns 当前项目上下文。
   */
  function resolveProjectContext(): AssemblyBranchNodeListProjectContext {
    const context = options.getProjectContext();
    if (!context || context.projectId <= 0n || context.repositoryId <= 0n) {
      throw new Error('缺少项目仓库上下文，无法调用分支与节点接口');
    }

    return context;
  }

  /**
   * 读取指定节点仓库的分支列表。
   *
   * @param repositoryId 目标节点仓库 ID。
   * @returns 前端负责分支摘要列表。
   */
  async function listRepositoryBranchSummaries(
    repositoryId: bigint,
  ): Promise<readonly AssemblyChildNodeBranchSummary[]> {
    const context = resolveProjectContext();
    const request = create(ListRepositoryBranchesRequestSchema, {
      projectId: context.projectId,
      repositoryId,
      branchKind: BRANCH_NODE_BRANCH_KIND,
    });
    const response = await cadClient.listRepositoryBranches(request);

    return response.branches
      .map((branch) => toBranchSummary(branch))
      .filter((branch): branch is AssemblyChildNodeBranchSummary => branch !== null);
  }

  /**
   * 获取当前装配分支下的直接子节点列表。
   *
   * @param input 当前装配体与分支上下文。
   * @returns 子节点摘要列表。
   */
  async function listChildNodes(
    input: ListAssemblyChildNodesInput,
  ): Promise<readonly AssemblyChildNodeSummary[]> {
    const context = resolveProjectContext();
    const branchId = parsePositiveBigIntId(input.branchId, 'branchId');
    const commitId = input.assemblyCommitId?.trim() || context.currentCommitId?.trim() || '';
    const request = create(ListBranchNodesRequestSchema, {
      branchId,
      page: 1,
      limit: childNodePageSize,
      commitId,
    });
    const response = await cadClient.listBranchNodes(request);
    const branchSummariesByRepository = new Map<string, Promise<readonly AssemblyChildNodeBranchSummary[]>>();

    function getBranchSummaries(repositoryId: bigint): Promise<readonly AssemblyChildNodeBranchSummary[]> {
      const cacheKey = repositoryId.toString();
      const cached = branchSummariesByRepository.get(cacheKey);
      if (cached) {
        return cached;
      }
      const promise = listRepositoryBranchSummaries(repositoryId);
      branchSummariesByRepository.set(cacheKey, promise);
      return promise;
    }

    return await mapWithConcurrency(response.nodes, 4, async (node) => {
      const branches = await getBranchSummaries(node.repositoryId);
      return toChildNodeSummary(node, branches);
    });
  }

  /**
   * 获取某个子节点的提交记录。
   *
   * @param input 当前装配体、分支与子节点上下文。
   * @returns 子节点提交记录列表。
   */
  async function listNodeCommits(
    input: ListAssemblyChildNodesInput & { readonly nodeId: string },
  ): Promise<readonly AssemblyChildNodeCommit[]> {
    const nodeKey = decodeAssemblyNodeKey(input.nodeId);
    const request = create(ListCommitsRequestSchema, {
      repositoryId: nodeKey.repositoryId,
      // 切换子节点版本需要看到该节点仓库下所有普通分支提交；传 branchId 会把结果限制在当前采纳分支。
      branchKind: BRANCH_NODE_BRANCH_KIND,
      page: 1,
      limit: nodeCommitPageSize,
    });
    const response = await versionClient.listCommits(request);
    const activeBranch = nodeKey.adoptedBranchId > 0n
      ? response.branches.find((branch) => branch.branchId === nodeKey.adoptedBranchId)
      : response.branches[0];
    const branchByCommitId = buildCommitBranchMap(response.commits, response.branches);

    return response.commits.map((commit) => (
      toNodeCommit(commit, input.nodeId, branchByCommitId.get(commit.commitId.trim()) ?? activeBranch)
    ));
  }

  /**
   * 切换父装配体对子节点采纳的提交版本。
   *
   * @param input 采纳版本切换输入。
   * @returns 更新后的子节点摘要。
   */
  async function switchAdoptedNodeVersion(
    input: SwitchAdoptedNodeVersionInput,
  ): Promise<AssemblyChildNodeSummary> {
    const context = resolveProjectContext();
    const nodeKey = decodeAssemblyNodeKey(input.nodeId);
    if (nodeKey.adoptedBranchId <= 0n || !nodeKey.adoptedCommitId.trim() || !nodeKey.instanceUuid.trim()) {
      throw new Error(`节点缺少当前采纳版本上下文，无法切换：nodeId=${input.nodeId}`);
    }

    // 协议已移除 CadService.SetAssemblyNodeAdoptedVersion。这里在 service 边界抛出明确错误，
    // 避免页面层伪造“切换成功”；后端提供新的写入口后，只需要替换本方法内的请求适配。
    throw new Error(
      `当前协议缺少子节点采纳版本写接口，无法切换采纳版本：projectId=${context.projectId.toString()}，repositoryId=${nodeKey.repositoryId.toString()}，branchId=${nodeKey.adoptedBranchId.toString()}，commitId=${input.commitId}`,
    );
  }

  return {
    listChildNodes,
    listNodeCommits,
    switchAdoptedNodeVersion,
  };
}
